import { SortType } from "../SortType";

export enum ProductListSortType {
  ASC = "ASC",
  DESC = "DESC",
}

export const PRODUCT_SORT_COLUMN_MAP: Record<string, string> = {
  id: "p.id",
  name: "p.name",
  selling_price: "p.selling_price",
  cost_price: "p.cost_price",
  priority: "p.priority",
  created_date: "p.created_date",
  modified_date: "p.modified_date",
  active: "p.active",
  display: "p.display",
};

export interface ProductListApi {
  id: number;
  name: string;
  category_id: number;
  category_name: string | null;
  preferred_vendor_id: number | null;
  preferred_vendor_name: string | null;
  sku: string | null;
  cost_price: number;
  selling_price: number;
  hsn_code: string | null;
  gst_percentage: number | null;
  display: boolean;
  active: boolean;
  default_image_url: string | null;
  created_by: number | null;
  created_by_name: string | null;
  last_modified_by: number | null;
  last_modified_by_name: string | null;
  created_date: Date | string | null;
  modified_date: Date | string | null;
}

export interface ProductCategoryApi {
  category_id: number;
  category_name: string;
  is_primary: boolean;
  active: boolean;
  markup: number | null;
}

export interface ProductVendorApi {
  vendor_id: number;
  vendor_name: string;
  is_preferred: boolean;
  active: boolean;
}

export interface ProductImageApi {
  id: number;
  image_url: string;
  preferred: boolean;
  is_default: boolean;
  active: boolean;
}

export interface ProductVariantImageApi {
  id: number;
  image_url: string;
  active: boolean;
}

export interface ProductVariantApi {
  id: number;
  options_id?: number;
  option_name: string | null;
  name: string;
  title: string | null;
  hex_code: string | null;
  cost_price: number | null;
  selling_price: number | null;
  sku: string | null;
  active: boolean;
  /** Option value IDs for this variant, ordered by category option display_order. */
  option_detail_ids?: number[];
  images?: ProductVariantImageApi[];
}

export interface ProductPrintingTypeApi {
  id: number;
  printing_type_id: number;
  printing_type_name: string | null;
  active: boolean;
}

export interface UrgentSourcingProductApi {
  id: number;
  name: string;
  timeline: number;
  price_without_tax: number;
  fifty_quantity_price_without_tax: number;
  category_id: number;
  gst: number;
  price_with_gst: number;
  selling_price: number;
  hsn: string | null;
  description: string | null;
  vendor_name: string | null;
  vendor_phone_number: string | null;
  added_by: number | null;
  is_admin_product: boolean;
  admin_product_id: number | null;
  active: boolean;
  modified_date: Date | string | null;
  created_date: Date | string | null;
}
export interface ProductDetailApi extends ProductListApi {
  zoho_id: string | null;
  vendor_code: string | null;
  brand_name: string | null;
  dimensions: string | null;
  size_chart_url: string | null;
  sizes: string | null;
  capacity: string | null;
  desc: string | null;
  colors: string | null;
  priority: number;
  fast_procurement: boolean;
  preferred_printing_type_id: number | null;
  print_on_demand: boolean;
  is_best_selling: boolean;
  length: number | null;
  breadth: number | null;
  height: number | null;
  weight: number | null;
  seo_title: string | null;
  seo_description: string | null;
  categories: ProductCategoryApi[];
  vendors: ProductVendorApi[];
  images: ProductImageApi[];
  variants: ProductVariantApi[];
  printing_types: ProductPrintingTypeApi[];
  urgent_sourcing_details: UrgentSourcingProductApi;
}

const SORT_BY_FIELDS = [
  "id",
  "name",
  "selling_price",
  "cost_price",
  "priority",
  "created_date",
  "modified_date",
  "active",
  "display",
] as const;

export type ProductListSortBy = (typeof SORT_BY_FIELDS)[number];

export const PRODUCT_SORT_BY_OPTIONS: {
  value: ProductListSortBy;
  label: string;
}[] = [
  { value: "id", label: "ID" },
  { value: "name", label: "Name" },
  { value: "selling_price", label: "Selling price" },
  { value: "cost_price", label: "Cost price" },
  { value: "priority", label: "Priority" },
  { value: "created_date", label: "Created" },
  { value: "modified_date", label: "Updated" },
  { value: "active", label: "Active" },
  { value: "display", label: "Display" },
];

export function normalizeProductListSortBy(
  raw: string | null | undefined,
): ProductListSortBy {
  if (raw && (SORT_BY_FIELDS as readonly string[]).includes(raw)) {
    return raw as ProductListSortBy;
  }
  return "id";
}

export interface ProductListFilters {
  id?: number;
  /** One or more category ids (OR). Matches primary or active additional mapping. */
  category_id?: number[];
  preferred_vendor_id?: number;
  active?: 0 | 1;
  display?: 0 | 1;
  search?: string;
  min_selling_price?: number;
  max_selling_price?: number;
  page_no: number;
  limit: number;
  sort_by: ProductListSortBy;
  sort_type: SortType;
}

export interface CreateProductInput {
  name: string;
  category_id: number;
  preferred_vendor_id?: number | null;
  cost_price: number;
  selling_price: number;
  sku?: string | null;
  zoho_id?: string | null;
  desc?: string | null;
  display?: number;
  hsn_code?: string | null;
  gst_percentage?: number | null;
  fast_procurement?: number;
  preferred_printing_type_id?: number | null;
  category_ids?: number[];
}

export interface UpdateProductInput {
  name?: string;
  category_id?: number;
  preferred_vendor_id?: number | null;
  cost_price?: number;
  selling_price?: number;
  sku?: string | null;
  zoho_id?: string | null;
  desc?: string | null;
  display?: number;
  brand_name?: string | null;
  dimensions?: string | null;
  sizes?: string | null;
  capacity?: string | null;
  colors?: string | null;
  priority?: number;
  length?: number | null;
  breadth?: number | null;
  height?: number | null;
  weight?: number | null;
  print_on_demand?: number;
  is_best_selling?: number;
}

export interface UpdateSourcingInput {
  zoho_id?: string | null;
  hsn_code?: string | null;
  gst_percentage?: number | null;
  fast_procurement?: number;
  preferred_printing_type_id?: number | null;
  preferred_vendor_id?: number | null;
  default_printing_cost?: number | null;
}

export interface UpdateSeoInput {
  seo_title: string;
  seo_description: string;
}

export interface UpdateImagesInput {
  size_chart_url?: string | null;
  images: Array<{
    id?: number;
    image_url: string;
    is_default?: number;
    preferred?: number;
    active?: number;
  }>;
}

export interface UpdateVariantImagesInput {
  images: Array<{
    id?: number;
    image_url: string;
    active?: number;
  }>;
}

export interface UpdateVariantsInput {
  variants: Array<{
    id?: number;
    name: string;
    title?: string | null;
    hex_code?: string | null;
    /** Option value ids for this variant, ordered by category option display_order. */
    option_detail_ids: number[];
    cost_price?: number | null;
    selling_price?: number | null;
    active?: number;
  }>;
}

export interface UpdateCategoriesInput {
  category_id: number;
  categories: Array<{
    category_id: number;
    active?: number;
    markup?: number | null;
  }>;
}

export interface UpdateVendorsInput {
  preferred_vendor_id?: number | null;
  vendors: Array<{
    vendor_id: number;
    active?: number;
  }>;
}

export interface UpdatePrintingTypesInput {
  preferred_printing_type_id?: number | null;
  printing_types: Array<{
    printing_type_id: number;
    active?: number;
  }>;
}

/** One selected option value used when building variant combinations. */
export interface OptionValueSelection {
  option_id: number;
  option_name: string;
  display_order: number;
  value_id: number;
  value: string;
  hex_code?: string | null;
}

/** Cartesian product of selected values grouped by option (display order). SKUs are server-generated. */
export function buildVariantCombos(
  byOption: Array<{
    option_id: number;
    option_name: string;
    display_order: number;
    values: Array<{
      value_id: number;
      value: string;
      hex_code?: string | null;
    }>;
  }>,
): UpdateVariantsInput["variants"] {
  const options = byOption
    .filter((o) => o.values.length > 0)
    .sort((a, b) => a.display_order - b.display_order);

  if (options.length === 0) return [];

  let combos: OptionValueSelection[][] = [[]];
  for (const opt of options) {
    const next: OptionValueSelection[][] = [];
    for (const prefix of combos) {
      for (const val of opt.values) {
        next.push([
          ...prefix,
          {
            option_id: opt.option_id,
            option_name: opt.option_name,
            display_order: opt.display_order,
            value_id: val.value_id,
            value: val.value,
            hex_code: val.hex_code ?? null,
          },
        ]);
      }
    }
    combos = next;
  }

  return combos.map((selections) => {
    const ordered = [...selections].sort(
      (a, b) => a.display_order - b.display_order || a.option_id - b.option_id,
    );
    const last = ordered[ordered.length - 1];
    const title = ordered.map((s) => s.value).join(" / ");
    return {
      name: last.value,
      title,
      hex_code: last.hex_code ?? null,
      option_detail_ids: ordered.map((s) => s.value_id),
      active: 1,
    };
  });
}

/** POD catalogue + variants from GET `/v1/catalog/products/:id/pod`. */
export interface ProductPodVariantApi {
  id: number;
  product_id: number;
  variant_id: number | null;
  sku: string;
  /** Explicit catalog SKU alias when API provides it. */
  variant_sku?: string;
  active: boolean;
  variant_name: string | null;
  color?: string | null;
  size?: string | null;
  created_by_admin_id: number | null;
  updated_by_admin_id: number | null;
  created_date: Date | string | null;
  modified_date: Date | string | null;
}

export interface ProductPodApi {
  id: number;
  product_id: number;
  product_sku: string | null;
  category_id: number;
  /** Preferred SoT field when present. */
  pod_selling_price?: number;
  /** API alias of pod_selling_price (legacy). */
  swageazy_selling_price: number;
  production_time: number | null;
  active: boolean;
  print_on_demand?: boolean;
  /** Deprecated alias of print_on_demand. */
  is_catalogue_product?: boolean;
  created_by_admin_id: number | null;
  updated_by_admin_id: number | null;
  created_date: Date | string | null;
  modified_date: Date | string | null;
  variants: ProductPodVariantApi[];
}

export interface UpdatePodInput {
  /** Preferred write field. */
  pod_selling_price?: number;
  /** Deprecated alias of pod_selling_price. */
  swageazy_selling_price?: number;
  production_time?: number | null;
  print_on_demand?: number;
  is_catalogue_product?: number;
}

export interface UpdatePodVariantsInput {
  variants: Array<{
    id?: number;
    /** `product_options.id` / product variant id */
    variant_id: number;
    /** Defaults to product_options.sku when omitted. */
    sku?: string;
    active?: number;
  }>;
}

/** Stable key for comparing variants by their option value ids. */
export function variantOptionDetailsKey(
  optionDetailIds: number[] | undefined | null,
): string {
  if (!optionDetailIds?.length) return "";
  return optionDetailIds.join(",");
}

export function asBool(v: unknown): boolean {
  return v === true || v === 1 || v === "1" || v === "true";
}

export function asFlag(v: unknown): number {
  return asBool(v) ? 1 : 0;
}

export function getApiErrorMessage(
  error: unknown,
  fallback = "Something went wrong",
): string {
  const ax = error as {
    response?: { data?: { message?: string }; status?: number };
    message?: string;
  };
  return ax?.response?.data?.message || ax?.message || fallback;
}
